import React, { useState, useEffect } from 'react';
import './Profile.css';
import { getUserByUuid, updateUser } from '../../services/userService';
import { jwtDecode } from 'jwt-decode';
import { useSelector } from 'react-redux';
import { ip, coordinates } from '../../Store/userSlice';
import { Snackbar, Alert } from '@mui/material';

const Profile = () => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    email: '',
    gstin: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const IpAddress = useSelector(ip);
  const userCoordinates = useSelector(coordinates);
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');        
        if (!token) {
          throw new Error('Authentication token not found');
        }
        
        const localUserData = JSON.parse(localStorage.getItem('user') || '{}');        
        let userUuid = localUserData.uuid;
        
        if (!userUuid && token) {
          try {
            const tokenPayload = jwtDecode(token);
            userUuid = tokenPayload.uuid;
          } catch (err) {
            console.error('Error parsing token:', err);
          }
        }                
        if (userUuid) {
          const response = await getUserByUuid(userUuid);          
          const userData = response.result || response;          
          const updatedFormData = { ...formData };
          
          if (userData.username) updatedFormData.name = userData.username;
          if (userData.phoneNumber) updatedFormData.phoneNumber = userData.phoneNumber;
          if (userData.email) updatedFormData.email = userData.email;
          if (userData.gstin) updatedFormData.gstin = userData.gstin;
          if (userData.address) updatedFormData.address = userData.address;
          
          setFormData(updatedFormData);
          
          if (!localUserData.uuid || Object.keys(localUserData).length === 0) {
            const updatedLocalData = { 
              ...localUserData, 
              uuid: userUuid,
              username: userData.username || localUserData.username,
              phoneNumber: userData.phoneNumber || localUserData.phoneNumber,
              email: userData.email || localUserData.email,
              gstin: userData.gstin || localUserData.gstin,
              address: userData.address || localUserData.address,
              roleId: userData.roleId || localUserData.roleId
            };
            localStorage.setItem('user', JSON.stringify(updatedLocalData));
          }
        } else {
          if (localUserData) {
            const updatedFormData = { ...formData };
            
            if (localUserData.username) updatedFormData.name = localUserData.username;
            if (localUserData.phoneNumber) updatedFormData.phoneNumber = localUserData.phoneNumber;
            if (localUserData.email) updatedFormData.email = localUserData.email;
            if (localUserData.gstin) updatedFormData.gstin = localUserData.gstin;
            if (localUserData.address) updatedFormData.address = localUserData.address;
            
            setFormData(updatedFormData);
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError(error.message);
        
        try {
          const userData = JSON.parse(localStorage.getItem('user') || '{}');
          if (userData && Object.keys(userData).length > 0) {
            const updatedFormData = { ...formData };
            
            if (userData.username) updatedFormData.name = userData.username;
            if (userData.phoneNumber) updatedFormData.phoneNumber = userData.phoneNumber;
            if (userData.email) updatedFormData.email = userData.email;
            if (userData.gstin) updatedFormData.gstin = userData.gstin;
            if (userData.address) updatedFormData.address = userData.address;
            
            setFormData(updatedFormData);
          }
        } catch (e) {
          console.error('Error parsing user data from localStorage:', e);
        }
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    fetchUserData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Get existing user data
      const userData = JSON.parse(localStorage.getItem('user')) || {};
      const userUuid = userData.uuid;
      
      if (!userUuid) {
        throw new Error('User UUID not found');
      }
      
      const updateData = {
        uuid: userUuid,
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        email: formData.email,
        username: formData.name, 
        ip: IpAddress,
        longitude: userCoordinates?.lng || "",
        latitude: userCoordinates?.lat || "",
        roleId: userData.roleId, 
        address: formData.address,
        gstin: formData.gstin
      };
      
      const response = await updateUser(updateData);
      
      if (response?.responseMessage?.toLowerCase().includes("phone number is already used") ||
          response?.responseMessage?.toLowerCase().includes("email is already used")) {
        setSnackbar({
          open: true,
          message: response.responseMessage,
          severity: 'error'
        });
        return;
      }
      
      const updatedUserData = { ...userData };
      
      // Only update fields that were filled in the form
      if (formData.name) updatedUserData.username = formData.name;
      if (formData.phoneNumber) updatedUserData.phoneNumber = formData.phoneNumber;
      if (formData.email) updatedUserData.email = formData.email;
      if (formData.address) updatedUserData.address = formData.address;
      if (formData.gstin) updatedUserData.gstin = formData.gstin;
      
      localStorage.setItem('user', JSON.stringify(updatedUserData));
      
      setSnackbar({
        open: true,
        message: 'Profile updated successfully!',
        severity: 'success'
      });
      
      await fetchUserData();
    } catch (error) {
      console.error('Error updating user data:', error);
      setSnackbar({
        open: true,
        message: error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-wrapper">
      <div className="profile-card">
        {loading ? (
          <div className="loading-indicator">Loading profile data...</div>
        ) : error ? (
          <div className="error-message">Error loading profile: {error}</div>
        ) : (
          <>
            <div className="profile-image-container">
              <div className="profile-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 24 24" fill="#0052CC">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z"/>
                  <path d="M12 14C7.58172 14 4 17.5817 4 22H20C20 17.5817 16.4183 14 12 14Z"/>
                </svg>
              </div>
              <div className="camera-button">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 7.5C3 6.67157 3.67157 6 4.5 6H6.37868C6.7765 6 7.15804 5.84196 7.43934 5.56066L9.43934 3.56066C9.72064 3.27936 10.1022 3.12132 10.5 3.12132H13.5C13.8978 3.12132 14.2794 3.27936 14.5607 3.56066L16.5607 5.56066C16.842 5.84196 17.2235 6 17.6213 6H19.5C20.3284 6 21 6.67157 21 7.5V18C21 18.8284 20.3284 19.5 19.5 19.5H4.5C3.67157 19.5 3 18.8284 3 18V7.5Z" stroke="white" strokeWidth="1.5"/>
                  <path d="M12 16.5C14.0711 16.5 15.75 14.8211 15.75 12.75C15.75 10.6789 14.0711 9 12 9C9.92893 9 8.25 10.6789 8.25 12.75C8.25 14.8211 9.92893 16.5 12 16.5Z" stroke="white" strokeWidth="1.5"/>
                </svg>
              </div>
            </div>
            
            {successMessage && (
              <div className="success-message">
                {successMessage}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="profile-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name<span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number<span className="required">*</span></label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="+91 9876-5432-10"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email<span className="required">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email ID"
                    required
                  />
                </div>
                {/* <div className="form-group">
                  <label htmlFor="gstin">GSTIN<span className="required">*</span></label>
                  <input
                    type="text"
                    id="gstin"
                    name="gstin"
                    value={formData.gstin}
                    onChange={handleInputChange}
                    placeholder="99AY48Y459047YV9"
                    required
                  />
                </div> */}
              </div>

              {/* <div className="form-group">
                <label htmlFor="address">Address<span className="required">*</span></label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Street, Locality, PIN, etc."
                  required
                />
              </div> */}

              <button type="submit" className="save-button" disabled={loading}>
                {loading ? 'Updating...' : 'Save Changes'}
              </button>
            </form>
          </>
        )}
      </div>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Profile;